import { Col, Row } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedNumber, injectIntl } from 'react-intl';
import { ClipboardCopy, EmailLink, FormItem, FormText, SapId } from 'ui';
import moment from 'utils/cops-moment';

const SoldTo = ({
  data: {
    number,
    name,
    name2,
    vatNumber,
    taxNumber,
    street,
    houseNumber,
    city,
    postalCode,
    language,
    countryName,
    phoneNumber,
    mobileNumber,
    email,
    remark,
    creditCheck,
    creditLimit,
    creditExposure,
    creditLimitCurrency,
    paymentTermsText,
    salesOfficeNumber,
    salesOfficeName,
    salesGroupNumber,
    salesGroupName,
    customerGroup,
    globalCustomer,
    ecsBlock,
    ecsValidFrom,
    ecsValidTo,
  },
  intl,
}) => {
  const ecsBlockValidFrom = moment(ecsValidFrom).isValid()
    ? moment(ecsValidFrom).utc()
    : null;

  const ecsBlockValidTo = moment(ecsValidTo).isValid()
    ? moment(ecsValidTo).utc()
    : null;

  return (
    <>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.NAME' })}>
            <FormText data-test-id='info-name'>{name}</FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.NAME_2' })}>
            <FormText>{name2}</FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.STREET' })}>
            <FormText>{street}</FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.HOUSE_NUMBER' })}>
            <FormText>{houseNumber}</FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.POSTAL_CODE' })}>
            <FormText>{postalCode}</FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.CITY' })}>
            <FormText>{city}</FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.COUNTRY' })}>
            <FormText>{countryName}</FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.EMAIL' })}>
            <ClipboardCopy onlyCopyThroughIcon={true}>
              <EmailLink href={`mailto:${email}`}>{email}</EmailLink>
            </ClipboardCopy>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.PHONE_NUMBER' })}>
            <FormText><ClipboardCopy>{phoneNumber}</ClipboardCopy></FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.MOBILE_NUMBER' })}>
            <FormText><ClipboardCopy>{mobileNumber}</ClipboardCopy></FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.SAP_NUMBER' })}>
            {/* use the ID component to tansform to 'readable' ID (without leading zeroes) */}
            <SapId originalId={number}>
              {id => <FormText data-test-id='sold-to.sapid'>{id}</FormText>}
            </SapId>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.LANGUAGE' })}>
            <FormText>{language}</FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.VAT_NUMBER' })}>
            <FormText data-test-id='sold-to.vat'>{vatNumber}</FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.TAX_NUMBER' })}>
            <FormText data-test-id='sold-to.tax'>{taxNumber}</FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.CREDIT_CHECK' })}>
            <FormText data-test-id='sold-to.creditcheck'>{creditCheck ? 'OK' : 'Not OK'}</FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.CREDIT_LIMIT' })}>
            <FormText>
              {creditLimit &&
                <span>
                  <FormattedNumber value={creditLimit} /> {creditLimitCurrency}
                </span>
              }
            </FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.CREDIT_EXPOSURE' })}>
            <FormText>
              {creditExposure &&
                <span>
                  <FormattedNumber value={creditExposure} /> {creditLimitCurrency}
                </span>
              }
            </FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.PAYMENT_TERMS' })}>
            <FormText>{paymentTermsText}</FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.SALES_OFFICE' })}>
            <FormText>{salesOfficeName || salesOfficeNumber}</FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.SALES_GROUP' })}>
            <FormText>{salesGroupName || salesGroupNumber}</FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.CUSTOMER_GROUP' })}>
            <FormText>{customerGroup}</FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.GLOBAL_CUSTOMER' })}>
            <FormText>{globalCustomer}</FormText>
          </FormItem>
        </Col>
      </Row>
      {
        ecsBlock && <Row type='flex' gutter={16}>
          <Col md={12} sm={24} xs={24}>
            <FormItem label={'ECS Valid From'}>
              <FormText>{ecsBlockValidFrom?.format('L')}</FormText>
            </FormItem>
          </Col>
          <Col md={12} sm={24} xs={24}>
            <FormItem label={'ECS Valid To'}>
              <FormText>{ecsBlockValidTo?.format('L')}</FormText>
            </FormItem>
          </Col>
        </Row>
      }
      <Row>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'common.labels.COMMENTS' })}>
            <FormText>{remark}</FormText>
          </FormItem>
        </Col>
      </Row>
    </>
  );
};

SoldTo.propTypes = {
  data: PropTypes.object,
  intl: PropTypes.object,
};

export default injectIntl(SoldTo);
